import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Brand-mark primitives, built on the ACTUAL Ashlar keystone artwork. Rendered
 * as inline SVG (not a CSS mask) so they can be two-tone and animate per piece.
 * Read `--g5-*` tokens, so they adapt light/dark. The old generic "geometric
 * house" motif is retired — the keystone is now the brand's recurring mark.
 */

export const MARK_SRC = '/ashlarapp-logo.svg'; // the icon mark (also the favicon)

// The dressed-stone mark: 6 "stone" strokes + the keystone crown polygon.
const STONE_PATHS = [
  'M13.52,17.01c-0.13,0-0.27-0.05-0.37-0.14c-0.1-0.08-0.16-0.19-0.19-0.3h-0.8l-4.52,8.45h3l1.29-2.49h3.54v-2.8h-2.08l1.17-2.18l-0.68-0.68C13.78,16.97,13.65,17.01,13.52,17.01z',
  'M18.86,16.88c-0.11,0.09-0.24,0.14-0.37,0.14c-0.13,0-0.26-0.04-0.36-0.13l-0.68,0.68l1.17,2.18h-2.08v2.8h3.54l1.29,2.49h3l-4.52-8.45h-0.8C19.02,16.69,18.95,16.8,18.86,16.88z',
  'M14.18,12.8l-1.88,3.51h0.65c0.02-0.09,0.06-0.18,0.12-0.26c0.12-0.14,0.28-0.21,0.45-0.21c0.13,0,0.27,0.05,0.37,0.14c0.14,0.11,0.21,0.28,0.21,0.45c0,0.08-0.02,0.17-0.06,0.25l0.65,0.65l1.11-2.05L14.18,12.8z',
  'M17.9,16.43c0-0.17,0.07-0.33,0.21-0.45c0.11-0.09,0.24-0.14,0.37-0.14c0.17,0,0.33,0.07,0.45,0.21c0.06,0.08,0.1,0.17,0.12,0.26h0.65l-1.88-3.51l-1.62,2.47l1.11,2.05l0.65-0.65C17.92,16.6,17.9,16.51,17.9,16.43z',
  'M13.52,16.11c-0.09,0-0.18,0.04-0.25,0.11c-0.05,0.06-0.07,0.13-0.07,0.21c0,0.09,0.04,0.18,0.11,0.25c0.06,0.05,0.13,0.08,0.21,0.08c0.09,0,0.18-0.04,0.25-0.11c0.05-0.06,0.08-0.13,0.08-0.21c0-0.09-0.04-0.18-0.11-0.25C13.66,16.13,13.59,16.11,13.52,16.11z',
  'M18.48,16.11c-0.07,0-0.14,0.02-0.21,0.08c-0.08,0.06-0.11,0.15-0.11,0.25c0,0.07,0.02,0.15,0.08,0.21c0.06,0.08,0.15,0.11,0.25,0.11c0.07,0,0.14-0.02,0.21-0.08c0.08-0.06,0.11-0.15,0.11-0.25c0-0.07-0.02-0.15-0.07-0.21C18.66,16.15,18.57,16.11,18.48,16.11z',
];
const KEY_POLY = '16,9.39 14.47,12.25 16,14.7 17.53,12.25';

/**
 * The keystone glyph — the brand's recurring "punctuation" mark. Use for list
 * bullets, chip leads, inline accents. Defaults to the keystone lavender; pass
 * `color` (e.g. "currentColor") to override.
 */
export function KeystoneMark({
  size = 16,
  color = 'var(--g5-key)',
  className,
  style,
}: {
  size?: number;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      aria-hidden
      className={className}
      width={size * 0.62}
      height={size}
      viewBox="0 0 31 50"
      style={{ display: 'inline-block', flexShrink: 0, verticalAlign: 'middle', ...style }}
    >
      <polygon points="15.5,0 0,22 15.5,50 31,22" fill={color} />
    </svg>
  );
}

/**
 * A calm section background: a soft diagonal brand wash anchored by a single
 * faint keystone mark bleeding from the bottom-right corner. Replaces the old
 * tiled house field — no repeat, no rigid grid. Position with `className`
 * (e.g. `absolute inset-0`). `tone="on-accent"` for use on accent-colored panels.
 */
export function BrandWatermark({
  className,
  tone = 'gradient',
  style,
}: {
  className?: string;
  tone?: 'gradient' | 'on-accent' | 'line';
  style?: React.CSSProperties;
}) {
  const onAccent = tone === 'on-accent';
  const wash = onAccent ? 'var(--g5-on-accent)' : 'var(--g5-accent)';
  const stone = onAccent ? 'var(--g5-on-accent)' : 'var(--g5-accent)';
  const key = onAccent ? 'var(--g5-on-accent)' : 'var(--g5-key)';
  const washPct = onAccent ? 14 : 9;
  return (
    <div aria-hidden className={cn('pointer-events-none overflow-hidden', className)} style={style}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(120deg, color-mix(in srgb, ${wash} ${washPct}%, transparent), transparent 62%)` }} />
      <svg viewBox="7.24 8.45 17.53 17.53" style={{ position: 'absolute', right: '-3%', bottom: '-16%', height: '128%', width: 'auto', opacity: onAccent ? 0.14 : 0.1 }}>
        <g fill={stone}>{STONE_PATHS.map((d, i) => <path key={i} d={d} />)}</g>
        <polygon fill={key} points={KEY_POLY} />
      </svg>
    </div>
  );
}

/**
 * The real keystone mark, shown at logo scale. Static = flat two-tone (stone +
 * keystone). When `animate`, it assembles upward in pieces, one-shot the first
 * time it scrolls into view (the loader motion). `tone` picks the paint:
 * `flow` = two-tone (default); `accent`/`accent2`/`on-accent` = a single quiet
 * tone for faint watermarks. Size via className/style. Decorative.
 */
export function BrandMarkWindow({
  className,
  animate = true,
  tone = 'flow',
  style,
}: {
  className?: string;
  animate?: boolean;
  tone?: 'flow' | 'accent' | 'accent2' | 'on-accent';
  style?: React.CSSProperties;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const [reduce] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  const [run, setRun] = useState(false);

  const wantsBuild = animate && !reduce;

  useEffect(() => {
    if (!wantsBuild) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setRun(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setRun(true); io.disconnect(); } });
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [wantsBuild]);

  const twoTone = tone === 'flow';
  const stone = twoTone ? 'var(--g5-accent)' : tone === 'on-accent' ? 'var(--g5-on-accent)' : tone === 'accent2' ? 'var(--g5-accent2)' : 'var(--g5-accent)';
  const key = twoTone ? 'var(--g5-key)' : stone;

  return (
    <svg
      ref={ref}
      aria-hidden
      viewBox="7.24 8.45 17.53 17.53"
      className={cn(wantsBuild && (run ? 'g5-mark-build' : 'g5-mark-pending'), className)}
      style={{ overflow: 'visible', ...style }}
    >
      {STONE_PATHS.map((d, i) => <path key={i} className="gm-stone" fill={stone} d={d} />)}
      <polygon className="gm-key" fill={key} points={KEY_POLY} />
    </svg>
  );
}
